import { yupResolver } from '@hookform/resolvers/yup'
import { CountryData } from 'country-codes-list'
import { map } from 'lodash-es'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import SelectField from '@/components/select-field'
import { Card } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormGroup } from '@/components/ui/form-group'
import { Input } from '@/components/ui/input'
import { Countries } from '@/core/constants/countries'
import { useEffect } from 'react'

export interface IBillingValues {
  address: string
  city: string
  country: string
  nationality: string
  postCode: string
  id?: string
  companyName?: string | null
  countryIso3Code?: string | null
  legalEntity?: boolean | null
  nationalityIso3Code?: string | null
  registerNumber?: string | null
  vatNumber?: string | null
}

export interface IShippingValues {
  address: string
  city: string
  country: string
  sameAsBilling: boolean
  postCode: string
  id?: string
}

interface BillingAddressFormProps {
  initialData?: Partial<IBillingValues>
  hasEditPermission: boolean
  onSubmit: (data: IBillingValues) => Promise<void>
}

interface ShippingAddressFormProps {
  initialData?: Partial<IShippingValues>
  billingData?: Partial<IBillingValues>
  hasEditPermission: boolean
  onSubmit: (data: IShippingValues) => Promise<void>
}

const addressSchemaObject = {
  id: yup.string().optional(),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  country: yup.string().required('Country is required'),
}

const billingAddressSchema = yup.object().shape({
  ...addressSchemaObject,
  nationality: yup.string().required('Nationality is required'),
  postCode: yup.string().required('Post Code is required'),
  companyName: yup.string().optional().nullable(),
  countryIso3Code: yup.string().optional().nullable(),
  legalEntity: yup.boolean().optional().nullable(),
  nationalityIso3Code: yup.string().optional().nullable(),
  registerNumber: yup.string().optional().nullable(),
  vatNumber: yup.string().optional().nullable(),
})

const shippingAddressSchema = yup.object().shape({
  ...addressSchemaObject,
  sameAsBilling: yup.boolean().required(),
  postCode: yup.string().required('Post Code is required'),
})

export const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  initialData,
  hasEditPermission,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<IBillingValues>({
    mode: 'onChange',
    resolver: yupResolver<IBillingValues>(billingAddressSchema),
    defaultValues: initialData,
  })

  useEffect(() => {
    if (initialData) {
      setValue('address', initialData.address || '')
      setValue('city', initialData.city || '')
      setValue(
        'country',
        initialData.country || initialData.countryIso3Code || '',
      )
      setValue('nationality', initialData.nationality || '')
      setValue('postCode', initialData.postCode || '')
    }
  }, [initialData])

  return (
    <Card className="p-[30px] bg-monochrome-4-60">
      <h2 className="text-2xl font-bold text-monochrome-1 mb-4">
        Billing Address
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormGroup label="Address" error={errors?.address?.message}>
            <Input
              isInvalid={!!errors?.address?.message}
              {...register('address')}
            />
          </FormGroup>
          <FormGroup label="City" error={errors?.city?.message}>
            <Input isInvalid={!!errors?.city?.message} {...register('city')} />
          </FormGroup>
          <FormGroup label="Country">
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={map(
                    Countries,
                    (country: CountryData, index: number) => ({
                      value: country.countryNameEn,
                      label: country.countryNameEn,
                    }),
                  )}
                  placeholder="Select country"
                  error={errors?.country?.message}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Nationality">
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={map(
                    Countries,
                    (country: CountryData, index: number) => ({
                      value: country.countryNameEn,
                      label: country.countryNameEn,
                    }),
                  )}
                  placeholder="Nationality"
                  error={errors?.nationality?.message}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Postal code" error={errors?.postCode?.message}>
            <Input
              isInvalid={!!errors?.postCode?.message}
              {...register('postCode')}
            />
          </FormGroup>
        </div>
        {hasEditPermission && (
          <>
            <hr className="my-4 border-monochrome-120" />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                className="min-w-[154px]"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" className="min-w-[154px]">
                Save
              </Button>
            </div>
          </>
        )}
      </form>
    </Card>
  )
}

export const ShippingAddressForm: React.FC<ShippingAddressFormProps> = ({
  initialData,
  billingData,
  hasEditPermission,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<IShippingValues>({
    mode: 'onChange',
    resolver: yupResolver<IShippingValues>(shippingAddressSchema),
    defaultValues: initialData,
  })

  useEffect(() => {
    if (initialData) {
      setValue('address', initialData.address || '')
      setValue('city', initialData.city || '')
      setValue('country', initialData.country || '')
      setValue('postCode', initialData.postCode || '')
    }
  }, [initialData])

  const selectedCountry = watch('country')
  const sameAsBilling = watch('sameAsBilling')

  // Update shipping address when sameAsBilling changes
  const handleSameAsBillingChange = (checked: boolean) => {
    setValue('sameAsBilling', checked)
    if (checked && billingData) {
      setValue('address', billingData.address || '')
      setValue('city', billingData.city || '')
      setValue('country', billingData.country || '')
      setValue('postCode', billingData.postCode || '')
    } else if (initialData) {
      setValue('address', initialData.address || '')
      setValue('city', initialData.city || '')
      setValue('country', initialData.country || '')
      setValue('postCode', initialData.postCode || '')
    }
  }

  return (
    <Card className="p-[30px] bg-monochrome-4-60">
      <h2 className="text-2xl font-semibold text-monochrome-1 mb-4">
        Shipping Address
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormGroup label="Address" error={errors?.address?.message}>
            <Input
              isInvalid={!!errors?.address?.message}
              {...register('address')}
              disabled={sameAsBilling}
              placeholder="Address"
            />
          </FormGroup>
          <FormGroup label="City" error={errors?.city?.message}>
            <Input
              isInvalid={!!errors?.city?.message}
              {...register('city')}
              disabled={sameAsBilling}
              placeholder="City"
            />
          </FormGroup>
          <FormGroup label="Country" error={errors?.country?.message}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <SelectField
                  outline
                  value={field.value}
                  onValueChange={field.onChange}
                  options={map(
                    Countries,
                    (country: CountryData, index: number) => ({
                      value: country.countryNameEn,
                      label: country.countryNameEn,
                    }),
                  )}
                  placeholder="Select country"
                  error={errors?.country?.message}
                  disabled={sameAsBilling}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Postal code" error={errors?.postCode?.message}>
            <Input
              isInvalid={!!errors?.postCode?.message}
              {...register('postCode')}
              disabled={sameAsBilling}
              placeholder="Postal code"
            />
          </FormGroup>
          <div className="flex items-center gap-2">
            <Checkbox
              id="sameAsBilling"
              checked={sameAsBilling}
              onCheckedChange={handleSameAsBillingChange}
            />
            <label htmlFor="sameAsBilling" className="text-sm cursor-pointer">
              Same as Billing Address
            </label>
          </div>
        </div>

        <hr className="my-4 border-monochrome-120" />
        <div className="flex justify-end items-center gap-2">
          {hasEditPermission && (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="min-w-[154px]"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" className="min-w-[154px]">
                Save
              </Button>
            </div>
          )}
        </div>
      </form>
    </Card>
  )
}
